import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

// One grid cell: shows the formatted value; admins click to type a correction (kept with who/when, like editing the sheet).
export function EditableCell({ row, col, canEdit, onSave, className = "", testId }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const raw = row[col.key];
  const overridden = row.overrides?.[col.key];
  const display = col.fmt(raw);
  const title = overridden ? `Edited by ${overridden.by} · ${new Date(overridden.at).toLocaleString("en-GB")}${overridden.note ? ` · ${overridden.note}` : ""} (was ${overridden.previous ?? "—"})` : undefined;

  if (!canEdit) {
    return <td className={`px-2 py-1 text-right font-mono tabular-nums whitespace-nowrap ${className}`} title={title} data-testid={testId}>{display}{overridden && <span className="ml-0.5 text-[9px] text-sky-600 align-super">●</span>}</td>;
  }
  const save = async () => {
    if (val === "") return;
    setBusy(true);
    try {
      await onSave(col.key, val, note);
      setOpen(false); setVal(""); setNote("");
    } finally { setBusy(false); }
  };
  return (
    <td className={`px-2 py-1 text-right font-mono tabular-nums whitespace-nowrap ${className}`} title={title} data-testid={testId}>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setVal(raw ?? ""); }}>
        <PopoverTrigger asChild>
          <button type="button" className="w-full text-right rounded px-0.5 hover:bg-sky-50 hover:ring-1 hover:ring-sky-200 cursor-text" data-testid={testId ? `${testId}-edit` : undefined}>
            {display}{overridden && <span className="ml-0.5 text-[9px] text-sky-600 align-super">●</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3 space-y-2" align="end">
          <p className="text-xs font-semibold text-slate-800">{col.label} · {row.date}</p>
          <Input type="number" step={col.kind === "int" ? 1 : 0.01} value={val} onChange={(e) => setVal(e.target.value)} className="h-8 font-mono" autoFocus data-testid="report-cell-input" onKeyDown={(e) => e.key === "Enter" && save()} />
          <Input placeholder="Why? (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="h-8 text-xs" data-testid="report-cell-note" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" className="h-7" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" className="h-7 bg-slate-900 hover:bg-slate-800" onClick={save} disabled={busy || val === ""} data-testid="report-cell-save">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </td>
  );
}
